import PropTypes from 'prop-types';
import FancyTimeStamp from '../../../../FancyTimeStamp';
import './SystemMessageSeparator.css';
import logger from '../../../../../utils/logger';
import { GEMATIK_ROOM, SYSTEM_MESSAGE_VISIBILITY } from '../../../../../constants';
import { useI18n } from '../../../../../context/I18nContext';
import { useMatrixClient } from '../../../../../context/MatrixContext';
import { useDisambiguation } from '../../../../../context/DisambiguationContext';

const {
  SHOW_NAME_CHANGE,
  SHOW_TOPIC_CHANGE,
  SHOW_JOIN_RULES_CHANGE,
  SHOW_VISIBILITY_CHANGE,
  SHOW_POWER_LEVEL_CHANGE,
  SHOW_PINNED_EVENTS_CHANGE,
} = SYSTEM_MESSAGE_VISIBILITY;

const SystemMessageSeparator = ({ unixTime, msg }) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const { needsDisambiguation } = useDisambiguation();
  const myUserId = client?.getUserId();
  
  // Early validation after hooks
  if (typeof unixTime !== 'number' || Number.isNaN(unixTime)) {
    return null;
  }
  const type = msg?.getType ? msg.getType() : 'unknown';

  // Helper function to get display name with fallbacks (synchronous)
  const getDisplayName = (userId) => {
    if (!userId || userId === 'Someone') return 'Someone';
    
    // For member events, try both current content and previous content
    // Previous content often has the displayname before a state change (ban/leave/etc)
    const contentDisplayName = msg.getContent?.()?.displayname;
    if (contentDisplayName && contentDisplayName !== '') return contentDisplayName;
    
    const prevContentDisplayName = msg.getPrevContent?.()?.displayname;
    if (prevContentDisplayName && prevContentDisplayName !== '') return prevContentDisplayName;
    
    // Then try to get from room member
    const roomId = msg.getRoomId?.();
    if (roomId && client) {
      const room = client.getRoom(roomId);
      if (room) {
        const member = room.getMember(userId);
        if (member?.rawDisplayName) return member.rawDisplayName;
        if (member?.name) return member.name;
      }
    }
    
    // Try to get from client user cache - check multiple properties
    if (client && typeof client.getUser === 'function') {
      const user = client.getUser(userId);
      if (user?.displayName) return user.displayName;
      if (user?.rawDisplayName) return user.rawDisplayName;
    }
    
    // Extract localpart from userId as a better fallback (e.g., @alice:server.com -> alice)
    // This is better than "Someone" as it gives some identification
    if (userId.startsWith('@') && userId.includes(':')) {
      const localpart = userId.split(':')[0].substring(1);
      if (localpart) return localpart;
    }
    
    // Last resort fallback
    return 'Someone';
  };

  // Wrapper that appends MXID for disambiguation (best-effort for departed members)
  const getDisambiguatedName = (userId) => {
    const baseName = getDisplayName(userId);
    if (userId && userId !== 'Someone' && needsDisambiguation(userId)) {
      return `${baseName} (${userId})`;
    }
    return baseName;
  };

  const targetUserId = msg.getStateKey?.() || 'Someone';
  const newDisplayName = getDisambiguatedName(targetUserId);
  const sender = msg.getSender?.() || msg.getSender;

  // Get sender display name - msg.sender is a User object with .name property,
  // msg.getSender() returns the userId string
  // First try msg.sender.name for display name, then fall back to getDisplayName with userId
  const baseSenderName = (msg.sender?.rawDisplayName || msg.sender?.name)
    ? (msg.sender.rawDisplayName || msg.sender.name)
    : (typeof sender === 'string' ? getDisplayName(sender) : 'Someone');
  const senderUserId = typeof sender === 'string' ? sender : null;
  const senderDisplayName = (senderUserId && needsDisambiguation(senderUserId))
    ? `${baseSenderName} (${senderUserId})`
    : baseSenderName;
  
  let label;
  let extraClass = '';
  switch (type) {
    case 'm.room.create':
      label = (
        <>
          {t('conversationCreatedOn', { date: '' })}<FancyTimeStamp unixTime={unixTime} briefDate />
        </>
      );
      extraClass = 'system-message-separator--room-create';
      break;
    // Gematik custom events - always hide (duplicates of standard events)
    // Both are sent for compatibility, but only standard events are displayed.
    // See: gemSpec_TI-M_Basis_V1.1.2 section 5.5.1.1
    case GEMATIK_ROOM.ROOM_NAME:
    case GEMATIK_ROOM.ROOM_TOPIC:
      return null;
    case 'm.room.name': {
      if (!SHOW_NAME_CHANGE) {
        return null;
      }
      const newName = msg.getContent()?.name;
      const prevName = msg.getPrevContent?.()?.name;
      const isCurrentUser = sender === myUserId;
      
      if (newName) {
        if (prevName) {
          label = isCurrentUser
            ? t('youChangedRoomNameFrom', { prevName, newName })
            : t('changedRoomNameFrom', { sender: senderDisplayName, prevName, newName });
        } else {
          label = isCurrentUser
            ? t('youChangedRoomNameTo', { newName })
            : t('changedRoomNameFromTo', { sender: senderDisplayName, newName });
        }
      } else {
        label = isCurrentUser
          ? t('youRemovedRoomName')
          : t('removedRoomNameBy', { sender: senderDisplayName });
      }
      break;
    }
    case 'm.room.topic': {
      if (!SHOW_TOPIC_CHANGE) {
        return null;
      }
      const newTopic = msg.getContent()?.topic;
      const prevTopic = msg.getPrevContent?.()?.topic;
      const isCurrentUser = sender === myUserId;
      
      if (newTopic) {
        if (prevTopic) {
          label = isCurrentUser
            ? t('youChangedRoomTopicFrom', { prevTopic, newTopic })
            : t('changedRoomTopicFrom', { sender: senderDisplayName, prevTopic, newTopic });
        } else {
          label = isCurrentUser
            ? t('youChangedRoomTopicTo', { newTopic })
            : t('changedRoomTopicTo', { sender: senderDisplayName, newTopic });
        }
      } else {
        label = isCurrentUser
          ? t('youRemovedRoomTopic')
          : t('removedRoomTopicBy', { sender: senderDisplayName });
      }
      break;
    }
    case 'm.room.avatar': {
      const prevAvatarUrl = msg.getPrevContent?.()?.url;
      const newAvatarUrl = msg.getContent?.()?.url;
      const isCurrentUser = sender === myUserId;

      if (!prevAvatarUrl && newAvatarUrl) {
        label = isCurrentUser
          ? t('youSetRoomAvatar')
          : t('setRoomAvatar', { sender: senderDisplayName });
      } else if (prevAvatarUrl && newAvatarUrl) {
        label = isCurrentUser
          ? t('youChangedRoomAvatar')
          : t('changedRoomAvatarBy', { sender: senderDisplayName });
      } else if (prevAvatarUrl && !newAvatarUrl) {
        label = isCurrentUser
          ? t('youRemovedRoomAvatar')
          : t('removedRoomAvatarBy', { sender: senderDisplayName });
      }
      break;
    }
    case 'm.room.member': {
      const content = msg.getContent?.() || {};
      const prevContent = msg.getPrevContent?.() || {};
      const membership = content.membership;
      const prevMembership = prevContent.membership;
      const avatarChanged =
        membership === 'join' &&
        prevMembership === 'join' &&
        content.avatar_url !== prevContent.avatar_url &&
        (content.avatar_url || prevContent.avatar_url);

      if (avatarChanged) {
        const isCurrentUser = targetUserId === myUserId;
        const profileChangeText = isCurrentUser
          ? t('youChangedYourProfilePicture')
          : t('changedTheirProfilePicture');
        const displayName = isCurrentUser ? t('you') : newDisplayName;

        label = `${displayName} ${profileChangeText}`;
      } else if (membership === 'join' && prevMembership !== 'join') {
        const isCurrentUser = targetUserId === myUserId;
        label = isCurrentUser ? t('youJoinedRoom') : t('userJoinedRoom', { user: newDisplayName });
      } else if (membership === 'leave' && prevMembership === 'join') {
        const isCurrentUser = targetUserId === myUserId;
        const isKick = sender !== targetUserId;

        if (isKick) {
          const kickerName = sender === myUserId ? t('you') : senderDisplayName;
          label = isCurrentUser
            ? t('kickedYouFromRoom', { kicker: kickerName })
            : kickerName === t('you')
              ? t('youKickedFromRoom', { user: newDisplayName })
              : t('kickedFromRoom', { kicker: kickerName, user: newDisplayName });
        } else {
          label = isCurrentUser ? t('youLeftRoom') : t('userLeftRoom', { user: newDisplayName });
        }
      } else if (membership === 'leave' && prevMembership === 'invite') {
        // Check if user declined or if invite was rescinded
        // If sender === targetUserId, the user declined the invite themselves
        // If sender !== targetUserId, someone else (the inviter or admin) rescinded it
        const isCurrentUser = targetUserId === myUserId;
        const userDeclined = sender === targetUserId;

        logger.debug('Processing invite decline/rescind', {
          membership,
          prevMembership,
          sender,
          targetUserId,
          userDeclined,
          isCurrentUser,
        });

        if (userDeclined) {
          label = isCurrentUser ? t('youDeclinedInvitation') : t('userDeclinedInvitation', { user: newDisplayName });
        } else {
          label = isCurrentUser
            ? t('invitationRescindedForYou', { sender: senderDisplayName })
            : t('invitationRescindedForUser', { sender: senderDisplayName, user: newDisplayName });
        }
      } else if (membership === 'invite') {
        // When re-inviting to a DM, the server sends TWO events:
        //   1. leave→invite (the actual re-invite)
        //   2. invite→invite (redundant state update, likely for is_direct flag)
        // We hide the second one as it creates duplicate "invited" messages in the UI
        if (prevMembership === 'invite') {
          return null;
        }
        const isCurrentUser = targetUserId === myUserId;
        const isSenderCurrentUser = sender === myUserId;
        
        if (isCurrentUser) {
          label = t('youWereInvited', { sender: senderDisplayName });
        } else if (isSenderCurrentUser) {
          label = t('youInvitedUser', { user: newDisplayName });
        } else {
          label = t('userWasInvited', { sender: senderDisplayName, user: newDisplayName });
        }
      } else if (membership === 'ban') {
        const isCurrentUser = targetUserId === myUserId;
        const isSenderCurrentUser = sender === myUserId;
        
        if (isCurrentUser) {
          label = t('userBannedYou', { sender: senderDisplayName });
        } else if (isSenderCurrentUser) {
          label = t('youBannedUser', { user: newDisplayName });
        } else {
          label = t('userBanned', { sender: senderDisplayName, user: newDisplayName });
        }
      } else if (membership === 'leave' && prevMembership === 'ban') {
        // User was unbanned
        const isCurrentUser = targetUserId === myUserId;
        const isSenderCurrentUser = sender === myUserId;

        if (isCurrentUser) {
          label = t('userUnbannedYou', { sender: senderDisplayName });
        } else if (isSenderCurrentUser) {
          label = t('youUnbannedUser', { user: newDisplayName });
        } else {
          label = t('userUnbanned', { sender: senderDisplayName, user: newDisplayName });
        }
      } else {
        logger.debug('Unhandled m.room.member membership change:', { content: msg.getContent() });
        return null;
      }
      break;
    }
    case 'm.room.join_rules': {
      if (!SHOW_JOIN_RULES_CHANGE) {
        return null;
      }
      const newRule = msg.getContent()?.join_rule;
      const isCurrentUser = sender === myUserId;
      
      if (newRule === 'public') {
        label = isCurrentUser
          ? t('youMadeRoomPublic')
          : t('madeRoomPublicBy', { sender: senderDisplayName });
      } else if (newRule === 'invite') {
        label = isCurrentUser
          ? t('youMadeRoomInviteOnly')
          : t('madeRoomInviteOnlyBy', { sender: senderDisplayName });
      } else if (newRule === 'knock') {
        label = isCurrentUser
          ? t('youChangedRoomToAllowKnocking')
          : t('changedRoomToAllowKnocking', { sender: senderDisplayName });
      } else if (newRule === 'restricted') {
        label = isCurrentUser
          ? t('youMadeRoomRestricted')
          : t('madeRoomRestricted', { sender: senderDisplayName });
      } else {
        label = isCurrentUser
          ? t('youChangedRoomAccessRules')
          : t('changedRoomAccessRules', { sender: senderDisplayName });
      }
      break;
    }
    case 'm.room.history_visibility': {
      if (!SHOW_VISIBILITY_CHANGE) {
        return null;
      }
      const visibility = msg.getContent()?.history_visibility;
      const isCurrentUser = sender === myUserId;
      
      if (visibility === 'world_readable') {
        label = isCurrentUser
          ? t('youMadeHistoryVisibleToAnyone')
          : t('madeHistoryVisibleToAnyone', { sender: senderDisplayName });
      } else if (visibility === 'shared') {
        label = isCurrentUser
          ? t('youMadeHistoryVisibleToAll')
          : t('madeHistoryVisibleToAll', { sender: senderDisplayName });
      } else if (visibility === 'invited') {
        label = isCurrentUser
          ? t('youMadeHistoryVisibleFromInvite')
          : t('madeHistoryVisibleFromInvite', { sender: senderDisplayName });
      } else if (visibility === 'joined') {
        label = isCurrentUser
          ? t('youMadeHistoryVisibleFromJoin')
          : t('madeHistoryVisibleFromJoin', { sender: senderDisplayName });
      } else {
        label = isCurrentUser
          ? t('youChangedHistoryVisibility')
          : t('changedHistoryVisibilityBy', { sender: senderDisplayName });
      }
      break;
    }
    case 'm.room.power_levels': {
      if (!SHOW_POWER_LEVEL_CHANGE) {
        return null;
      }
      const content = msg.getContent() || {};
      const prevContent = msg.getPrevContent?.() || {};
      const users = content.users || {};
      const prevUsers = prevContent.users || {};
      const isCurrentUser = sender === myUserId;
      
      // Matrix optimizes the power_levels state event by removing users from the explicit users map when they're set to the default level. If the user isn't in the explicit user map, use the default level instead.

      // Get default power levels for context
      const usersDefault = content.users_default ?? 0;
      const prevUsersDefault = prevContent.users_default ?? 0;

      // Find users whose power levels changed
      const changedUsers = [];
      
      // Check all users who appear in either current or previous state
      Object.keys({ ...users, ...prevUsers }).forEach(userId => {
        // Get effective power levels (explicit or default)
        const newLevel = users[userId] ?? usersDefault;
        const oldLevel = prevUsers[userId] ?? prevUsersDefault;
        
        if (newLevel !== oldLevel) {
          changedUsers.push({ userId, oldLevel, newLevel });
        }
      });

      if (changedUsers.length === 1) {
        const change = changedUsers[0];
        
        // Use getDisambiguatedName helper for consistent fallback behavior
        const userDisplayName = getDisambiguatedName(change.userId);
        
        if (change.newLevel === 100) {
          label = isCurrentUser
            ? t('youMadeUserAdmin', { user: userDisplayName })
            : t('madeUserAdmin', { sender: senderDisplayName, user: userDisplayName });
        } else if (change.newLevel === 50) {
          label = isCurrentUser
            ? t('youMadeUserModerator', { user: userDisplayName })
            : t('madeUserModerator', { sender: senderDisplayName, user: userDisplayName });
        } else if (change.newLevel === 0 && change.oldLevel === 100) {
          label = isCurrentUser
            ? t('youRevokedUserAdminStatus', { user: userDisplayName })
            : t('revokedUserAdminStatus', { sender: senderDisplayName, user: userDisplayName });
        } else if (change.newLevel === 0 && change.oldLevel === 50) {
          label = isCurrentUser
            ? t('youRevokedUserModeratorStatus', { user: userDisplayName })
            : t('revokedUserModeratorStatus', { sender: senderDisplayName, user: userDisplayName });
        } else {
          label = isCurrentUser
            ? t('youChangedUserPowerLevel', { user: userDisplayName, oldLevel: change.oldLevel, newLevel: change.newLevel })
            : t('changedUserPowerLevel', { sender: senderDisplayName, user: userDisplayName, oldLevel: change.oldLevel, newLevel: change.newLevel });
        }
      } else if (changedUsers.length > 1) {
        label = isCurrentUser
          ? t('youChangedPowerLevelsOfUsers', { count: changedUsers.length })
          : t('changedPowerLevelsOfUsers', { sender: senderDisplayName, count: changedUsers.length });
      } else {
        label = isCurrentUser
          ? t('youChangedRoomPermissions')
          : t('changedRoomPermissionsby', { sender: senderDisplayName });
      }
      break;
    }
    case 'm.room.pinned_events': {
      if (!SHOW_PINNED_EVENTS_CHANGE) {
        return null;
      }
      const pinnedEvents = msg.getContent()?.pinned || [];
      const prevPinnedEvents = msg.getPrevContent?.()?.pinned || [];
      const isCurrentUser = sender === myUserId;

      // Check "unpinned all" first before comparing lengths
      if (pinnedEvents.length === 0 && prevPinnedEvents.length > 0) {
        label = isCurrentUser
          ? t('youUnpinnedAllMessages')
          : t('unpinnedAllMessages', { sender: senderDisplayName });
      } else if (pinnedEvents.length > prevPinnedEvents.length) {
        label = isCurrentUser
          ? t('youPinnedMessage')
          : t('pinnedMessage', { sender: senderDisplayName });
      } else if (pinnedEvents.length < prevPinnedEvents.length) {
        label = isCurrentUser
          ? t('youUnpinnedMessage')
          : t('unpinnedMessage', { sender: senderDisplayName });
      } else {
        label = isCurrentUser
          ? t('youChangedPinnedMessages')
          : t('changedPinnedMessages', { sender: senderDisplayName });
      }
      break;
    }
    case 'm.room.tombstone': {
      const replacementRoom = msg.getContent()?.replacement_room;
      const isCurrentUser = sender === myUserId;
      
      if (replacementRoom) {
        label = isCurrentUser
          ? t('youUpgradedRoom')
          : t('upgradedRoom', { sender: senderDisplayName });
      } else {
        label = isCurrentUser
          ? t('youClosedRoom')
          : t('closedRoom', { sender: senderDisplayName });
      }
      break;
    }
    // Silent events - don't show in timeline
    case 'm.room.guest_access':
      return null;
    default:
      logger.debug(`Unhandled system message type: ${type}`, { msg });
      return null;
  }
  if (!label) {
    logger.debug(`No label generated for system message type: ${type}`, { msg });
    return null;
  }

  return (
    <div className={`system-message-separator${extraClass ? ' ' + extraClass : ''}`}>
      <span className="system-message-separator-label">{label}</span>
    </div>
  );
};

SystemMessageSeparator.propTypes = {
  unixTime: PropTypes.number.isRequired,
  msg: PropTypes.object.isRequired,
};

export default SystemMessageSeparator;
